<template>
  <div ref="main-product-tile" style="display: contents">
    <div
      class="d-flex w-100 h-100 pl-2"
      :class="{
        'spacebreak-disabled': isSpacebreakLocked(spacebreakId) || isEditingDisabled,
        'selected-product-list-view': isProductSelected,
      }"
    >
      <HighlightedArea v-if="product.isHighlighted" outer-class="w-100" list-view />

      <!-- Dynamic columns -->
      <v-col v-for="column in visibleListViewColumns" :key="column.id" :class="column.cellClass">
        <!-- Display row icons in a separate container, they have more complex layout -->
        <template v-if="column.id === 'rowIcons'">
          <v-icon
            v-if="showMinimumDistribution && product.minimumDistribution > 0"
            color="white"
            class="d-flex product-icon"
          >
            {{ 'mdi-speedometer' }}
          </v-icon>
          <div v-else class="product-icon product-icon--hidden h-100 distribution" />
          <div
            v-if="product.productSeries"
            class="d-flex product product-icon"
            style="cursor: pointer"
            @click="onProductSeriesIconClick"
          >
            <SeriesIcon />
          </div>
          <div v-else class="h-100 product-icon product-icon--hidden series" />
          <div v-if="hasPallets" class="product-icon">
            <PalletIcon :fill="hasAssignedPallets ? iconPrimary : iconDisabled" />
          </div>
          <div v-else class="product-icon product-icon--hidden h-100 pallet" />
          <div v-if="!inReset" class="d-flex product-icon">
            <NotInResetIcon color="white" />
          </div>
          <div v-else class="product-icon product-icon--hidden h-100 reset" />
          <v-icon
            :disabled="
              isSpacebreakLocked(spacebreakId) ||
              isEditingDisabled ||
              !inReset ||
              !canProductBeMoved
            "
            color="white"
            class="product-icon lock-icon"
            :class="{ 'lock-icon__locked': isLocked(product) }"
            @click="toggleLock(product)"
          >
            {{ isLocked(product) ? 'mdi-lock-outline' : 'mdi-lock-open-variant-outline' }}
          </v-icon>
        </template>

        <!-- Display tags in a separate container -->
        <template v-else-if="column.id === 'deltaTag'">
          <ProductDeltaTag
            :show="getReferenceDelta !== 0 && product.isEligible"
            type="R"
            :delta="getReferenceDelta"
            class="mr-1"
          />
          <AssortmentTag :tag-text="getProductTag" class="mr-1" />
          <ProductDeltaTag
            :show="isOptimised && getOptimisedDelta !== 0 && product.isEligible"
            type="O"
            :delta="getOptimisedDelta"
            class="mr-1"
          />
        </template>

        <!-- Display regular values -->
        <span v-else :title="product[column.id]">
          {{ getColumnValue(column) }}
        </span>
      </v-col>

      <!-- Static product info column -->
      <v-col class="pt-1 pb-1 d-flex justify-end product-info-column">
        <v-icon
          :id="`product-tile-popup-${product.productKey}`"
          class="info-icon mr-1"
          size="18"
          height="26px"
          width="26px"
          @click="event => openPopup(event)"
        >
          mdi-information
        </v-icon>
      </v-col>
    </div>

    <ProductDraggableTooltip
      v-if="show"
      :selected-kpis="kpisForDisplay"
      :product-info="product"
      :product-index="index"
      :pod-and-store-count-info="
        podAndStoreCounts(product, selectedCanvas.clusterId, spacebreaksIndexedByIdByCluster)
      "
      :product-positioning-info="
        extractProductPositioningInfo(
          product,
          selectedCanvas.clusterId,
          getSpacebreaksIndexedById,
          selectedPod
        )
      "
      :settings="tooltipSettings"
      :conditional-product-tile-style="dashboardProductTileSize"
      :image-url="productUtils.getImageUrl(clientConfig, product)"
      :image-title="displayText"
      :image-alt="displayText"
      :spacebreaks="spacebreaksNames"
      :selected-spacebreak="selectedSpacebreak"
      :spacebreak-disabled="isSpacebreakLocked(spacebreakId) || isEditingDisabled"
      :spacebreak-types="spacebreakTypes"
      :change-spacebreak="changeSpacebreak"
      :lock-types="lockTypes"
      :popup-button="popupButton"
      :image-tag-text="getProductTag"
      :product-pallets="productPallets"
      :current-numeric-locale="getCurrentNumericLocale"
      @close-popup="closePopup"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { get, isString, isEmpty, isNil, isUndefined, isNull } from 'lodash';

// enums
import assortmentProductLockTypes from '@enums/assortment-product-lock-types';
import SpacebreakTypes from '@enums/spacebreak-types';
import productKpis from '@enums/product-kpis';

// utils
import destroy from '@/utils/destroy';
import productUtils from '@/utils/product-utils';

// composables
import { useFeature } from '@/composables/use-feature';
import { useCanvasEditability } from '@/composables/use-canvas-editability';
import { useFormatNumber } from '@/composables/use-format-number';
import { useDayjs } from '@/composables/use-dayjs';
import { useDomUtility } from '@/composables/dom-utils';
// stores
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useContextStore } from '@/stores/context';
import { useFurnitureStore } from '@/stores/furniture';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';

// mixins
import productMixin from './product.mixin';

export default {
  mixins: [productMixin], // includes methods extractProductPositioningInfo

  props: {
    product: {
      type: Object,
      required: true,
    },

    spacebreakId: {
      type: String,
      default: null,
    },

    listViewRef: {
      type: Object,
      required: true,
    },

    index: {
      type: Number,
      required: true,
    },

    parentEntryItemRef: {
      type: Object,
      required: true,
    },

    openPopupOnLoadForProductId: {
      type: String,
      default: '',
    },
  },

  setup() {
    const { hasProductLineEnabled, hasKPKUEnabled } = useFeature();
    const { canvasCanBeEdited } = useCanvasEditability();
    const { formatDate } = useDayjs();
    const { formatNumber } = useFormatNumber();
    const { scrollToProductInCanvas, addActiveSearchBorderToProductElement } = useDomUtility();

    return {
      canvasCanBeEdited,
      hasProductLineEnabled,
      hasKPKUEnabled,
      formatDate,
      formatNumber,
      scrollToProductInCanvas,
      addActiveSearchBorderToProductElement,
      productUtils,
    };
  },

  data() {
    return {
      tooltipSettings: null,
      show: false,
      zIndex: null,
      get,
      isString,
      lockTypes: assortmentProductLockTypes.lockTypes,
      spacebreakTypes: SpacebreakTypes.spacebreakTypes,
      popupButton: {
        label: this.$t('assortmentCanvasPage.tooltip.productDashboard'),
        action: this.openProductDashboard,
      },
      productPallets: [],
      iconPrimary: 'white',
      iconDisabled: '#d9d9d9',
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'referenceCheckpointProducts',
      'popUpZindex',
      'selectedPod',
      'selectedDashboardProduct',
      'selectedCanvas',
      'isSpacebreakLocked',
      'canvasProductsPallets',
      'filteredListViewKpiColumns',
      'listViewExtraColumns',
      'visibleListViewColumns',
    ]),
    ...mapState(useContextStore, ['clientConfig', 'getCurrentNumericLocale', 'getDateFormats']),
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useFurnitureStore, ['getSpacebreaksIndexedById']),
    ...mapState(useWorkpackagesStore, ['isFreshWP']),

    kpisForDisplay() {
      const kpisToDisplay = [...this.clientConfig.selectedKpis];
      if (this.isFreshWP) {
        kpisToDisplay.push('shrinkToSalesRatio');
      }
      return kpisToDisplay;
    },

    // helper to get the product display string
    productDisplayString() {
      const displayString = `${this.product.productKeyDisplay}`;

      if (this.hasKPKUEnabled) {
        const extraIdentifiers = [this.product.kpku, this.product.futureId]
          .filter(el => !isNil(el))
          .join('/');
        if (extraIdentifiers.length) {
          return `${displayString} (${extraIdentifiers})`;
        }
      }
      return displayString;
    },

    // helper to get the combined UOM display string
    combinedUom() {
      return (
        this.formatNumber({ number: this.product.contentValue, format: 'float' }) +
        this.product.contentUnitOfMeasure
      );
    },

    // helper to get the volume UOM display string
    volumeUom() {
      const volume = this.product.averageTransactionWeight * this.product.contentValue;
      if (!volume) return '';
      return `${this.formatNumber({
        number: volume,
        format: 'float',
      })}${this.product.contentUnitOfMeasure}`;
    },

    isOptimised() {
      return get(this.selectedCanvas, 'hasBeenOptimised', false);
    },

    spacebreaksIndexedByIdByCluster() {
      return this.getSpacebreaksIndexedById[this.selectedCanvas.clusterId];
    },

    displayText() {
      return this.product.itemDescription || this.product.productKeyDisplay;
    },

    dashboardProductTileSize() {
      return {
        width: '80px',
        height: '80px',
      };
    },

    selectedSpacebreak() {
      return productUtils.selectedSpacebreak(
        this.spacebreaksNames,
        this.product.currentSpacebreakId
      );
    },

    isEditingDisabled() {
      return !this.canvasCanBeEdited;
    },

    inReset() {
      return get(this.product, 'inReset', false);
    },

    futureProductIdString() {
      return productUtils.futureProductIdString(this.product.kpku, this.product.futureId);
    },

    hasPallets() {
      return !isEmpty(this.productPallets);
    },

    hasAssignedPallets() {
      return this.hasPallets && this.productPallets.some(p => p.currentSpacebreakId);
    },

    getOptimisedDelta() {
      return get(this.product.deltas, 'lastOptimised', 0);
    },

    getReferenceDelta() {
      return get(this.product.deltas, 'reference', 0);
    },

    showMinimumDistribution() {
      return get(this.selectedScenario, 'optionalAttributes.useMinimumDistribution');
    },

    isProductSelected() {
      // there might not be a selected product
      return this.product.productKey === get(this.selectedDashboardProduct, 'productKey', null);
    },

    extraKPIsFormatFunctions() {
      return {
        wasteAndMarkdown: kpiValue => this.formatNumber({ number: kpiValue, format: 'currency' }),
        wasteLossAndMarkdown: kpiValue =>
          this.formatNumber({ number: kpiValue, format: 'currency' }),
        wasteAndMarkdownPercentage: kpiValue =>
          this.formatNumber({ number: kpiValue, format: '0.00%' }),
        wasteLossAndMarkdownPercentage: kpiValue =>
          this.formatNumber({ number: kpiValue, format: '0.00%' }),
        currentlyObservedSpacebreak: kpiValue => kpiValue,
        previouslyObservedSpacebreak: kpiValue => kpiValue,
        dateOfObservedSpacebreakChange: kpiValue =>
          this.formatDate(kpiValue, this.getDateFormats.long),
        chainIntroductionDate: kpiValue => this.formatDate(kpiValue, this.getDateFormats.long),
        volumeRoS: kpiValue => this.formatNumber({ number: kpiValue, format: 'float' }),
      };
    },
  },

  mounted() {
    // Detrimine if needs to open the popup on render
    if (this.openPopupOnLoadForProductId === this.product._id) {
      // Highlight the product
      const productTile = this.$refs['main-product-tile'];
      this.addActiveSearchBorderToProductElement(productTile);

      // Need to map the position of the element
      const position = {
        clientX: productTile.top,
        clientY: productTile.left + 120,
      };
      this.openPopup(position);
    }
    this.product.futureProductIdString = this.hasKPKUEnabled ? this.futureProductIdString : null;
    this.getProductPallets(this.product.productKeyDisplay);
  },

  beforeUnmount() {
    destroy.destroyReactiveVueProps(this);
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, [
      'updateCanvasProduct',
      'changeLockType',
      'incrementPopUpZindex',
      'setIsProductSidebarOpen',
    ]),

    getColumnValue(column) {
      const extraColumnIds = this.listViewExtraColumns.map(col => col.id);

      if (column.id === 'productKeyDisplay') {
        // Return the product display string for the product key column
        return this.productDisplayString;
      }
      if (column.id === 'contentValue') {
        // Return the combined unit of measure for the content value column
        return this.combinedUom;
      }
      if (column.id === 'transactionWeight') {
        // Return the volume unit of measure for the transaction weight column
        return this.volumeUom;
      }
      if (this.filteredListViewKpiColumns.includes(column.id)) {
        // Return the KPI value for columns that are in the filtered list view KPI columns
        return this.getKpiValue(column.id);
      }
      if (extraColumnIds.includes(column.id) && column.type === 'kpi') {
        return this.getExtraKpiValue(column.id);
      }

      // For all other columns, return the corresponding value from the product object
      return this.product[column.id];
    },

    // helper to get KPI value to display in a cell
    getKpiValue(kpi) {
      if (!isNull(this.product[kpi]) && !isUndefined(this.product[kpi])) {
        const kpiValue = this.product[kpi];
        if (kpi === productKpis.kpis.MARGIN) {
          if (!kpiValue) return '0%';

          const marginValueToShow = (kpiValue * 100).toFixed(0);

          return `${marginValueToShow}%`;
        }

        const val = this.formatNumber({ number: kpiValue, format: 'float' });
        const unit = kpi === productKpis.kpis.VOLUME ? this.product.contentUnitOfMeasure : '';
        return `${val}${unit}`;
      }
    },

    getExtraKpiValue(kpi) {
      if (isNull(this.product[kpi]) || isUndefined(this.product[kpi])) {
        return '';
      }
      const kpiValue = this.product[kpi];

      if (kpi === productKpis.kpis.SHRINK) {
        return `${kpiValue ? (kpiValue * 100).toFixed(0) : 0}%`;
      }

      const getFormattedExtraKpi = this.extraKPIsFormatFunctions[kpi];

      return getFormattedExtraKpi ? getFormattedExtraKpi(kpiValue) : kpiValue;
    },

    getProductPallets(productKeyDisplay) {
      this.productPallets = this.canvasProductsPallets[productKeyDisplay];
    },

    isLocked(product) {
      return product.lockType === assortmentProductLockTypes.lockTypes.locked || !this.inReset;
    },

    toggleLock(product) {
      this.changeLockType({ product, isLocked: this.isLocked(product) });
    },

    closePopup() {
      this.show = false;
    },

    openPopup() {
      const parentEntryItem = this.parentEntryItemRef['parent-entry-item'];

      // get pallets for the product
      this.getProductPallets(this.product.productKeyDisplay);

      const width = this.clientConfig.canvasPopUpWidth;
      const height = this.clientConfig.canvasPopUpHeight;

      this.incrementPopUpZindex();
      this.zIndex = this.popUpZindex;

      // Use getBoundingClientRect to get viewport-absolute coordinates needed for position:fixed (teleported to body)
      const entryRect = parentEntryItem.getBoundingClientRect();

      // Count currently open popups to cascade the new one so they don't overlap.
      const openPopupCount = document.querySelectorAll('.draggable-resizable').length;
      const multiplePopupOffset = openPopupCount * 20;

      // popup's right edge sits just left of the info icon column (50px wide),
      // so the icon remains clickable to open popups for other rows.
      const preferredLeft = entryRect.right - width - 50 - multiplePopupOffset;
      const preferredTop = entryRect.top;

      // Clamp to viewport so the popup is always fully visible regardless of scroll position or list length.
      const left = Math.max(0, Math.min(preferredLeft, window.innerWidth - width));
      const top = Math.max(0, Math.min(preferredTop, window.innerHeight - height));

      this.tooltipSettings = {
        left,
        top,
        width,
        height,
        zIndex: this.zIndex,
      };
      this.show = true;
    },

    onProductSeriesIconClick() {
      this.setIsProductSidebarOpen(true);
      this.setDashboardProduct(this.product);
      this.scrollToProductInCanvas(this.product);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.list-entry {
  width: 100%;
}

.icons-container-list-view {
  padding: 0;
}

.entry-col {
  font-size: 1.2rem;
  padding-left: 5px;
  padding-right: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  gap: 1rem;
}

.info-icon {
  color: $assortment-primary-colour;
}

.title {
  background-color: $canvas-list-entry-header-colour;
  border-radius: 4px;
  text-transform: uppercase;
}

.entry-item {
  display: flex;
  height: 28px; // affects the highlighted box
  align-items: center;
  position: relative;
  border-radius: 2px;
  border: 1px solid;
  margin-bottom: 5px;
  transition: all 1s ease-out;
}

.neutral-border {
  border-color: black;
}

.spacebreak-disabled {
  opacity: 30%;
}

.product-info-column {
  width: 50px;
}

.icons-container-list-view .product-icon {
  color: $product-icons-colour;
  height: 26px;
  width: 26px;
  margin-right: 1px;
  display: flex;
  align-items: center;
  justify-content: center;

  &--hidden {
    background-color: transparent;
  }
}

.list-view-tags-col {
  overflow: visible;

  :deep(span) {
    font-weight: 700;
  }
}

.col-short {
  max-width: 70px;
  max-width: 70px;
}

.col-xs {
  max-width: 28px;
  min-width: 28px;
}

.description-col {
  width: 260px;
}

.id-col {
  max-width: 100px;
  min-width: 100px;
}

.lock-icon {
  visibility: hidden;
  transition: 0s;

  &__locked {
    visibility: initial;
  }

  :deep(div) {
    height: 17px;
    width: 17px;
  }
}

.entry-item:hover {
  .lock-icon {
    visibility: initial;
  }
}
</style>
